import { TellescopeModelFromName, useUploadTellescopeFile } from "@tellescope/tanstack-query";
import { useUncontrolledState } from "@tellescope/ui";
import { getAttachmentType } from "@tellescope/utils";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useFilesFromSecureNames } from "./useFilesFromSecureNames";
import type { FileUploadStage } from "@tellescope/tanstack-query";

export type ChatAttachment = NonNullable<TellescopeModelFromName<"chats">["attachments"]>[number];

export interface UseInboxAttachmentsArgs {
  attachments?: ChatAttachment[];
  setAttachments?: Dispatch<SetStateAction<ChatAttachment[]>>;
  enduserId?: string;
}

export interface UseInboxAttachmentsReturn {
  attachments: ChatAttachment[];
  files: TellescopeModelFromName<"files">[];
  isLoading: boolean;
  isError: boolean;
  loadingCount: number;
  uploadFiles: (files: File[]) => Promise<void>;
  addExistingFiles: (files: TellescopeModelFromName<"files">[]) => void;
  removeAttachment: (secureName: string) => void;
  clearAttachments: () => void;
  uploadState: { stage: FileUploadStage; progress: number };
}

export const useMessagingAttachments = ({
  attachments: providedAttachments,
  setAttachments: providedSetAttachments,
  enduserId,
}: UseInboxAttachmentsArgs): UseInboxAttachmentsReturn => {
  const [attachments, setAttachments] = useUncontrolledState({
    state: providedAttachments ?? [],
    setState: providedSetAttachments,
  });

  const { files, isLoading, isError, loadingCount } = useFilesFromSecureNames(attachments);

  const uploadFileMutation = useUploadTellescopeFile({
    prepareMutationOptions: {},
    confirmMutationOptions: {},
  });

  const [uploadProgress, setUploadProgress] = useState(0);

  // Upload multiple files sequentially
  const uploadFiles = useCallback(
    async (filesToUpload: File[]) => {
      const uploadedAttachments: ChatAttachment[] = [];

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        if (!file) continue;
        try {
          const fileRecord = await uploadFileMutation.uploadFileAsync(
            {
              name: file.name,
              size: file.size,
              type: file.type,
              enduserId,
              publicRead: false,
              hiddenFromEnduser: false,
            },
            file
          );

          uploadedAttachments.push({
            type: getAttachmentType(fileRecord.type ?? ""),
            name: fileRecord.name,
            secureName: fileRecord.secureName,
          });

          // Update progress
          setUploadProgress(Math.round(((i + 1) / filesToUpload.length) * 100));
        } catch (error) {
          console.error(`Failed to upload file ${file.name}:`, error);
          throw error;
        }
      }

      // Add all uploaded attachments to the list
      setAttachments((prev) => [...prev, ...uploadedAttachments]);
      setUploadProgress(0);
    },
    [enduserId, uploadFileMutation, setAttachments]
  );

  // Add existing files (from the file picker)
  const addExistingFiles = useCallback(
    (filesToAdd: TellescopeModelFromName<"files">[]) => {
      const newAttachments: ChatAttachment[] = filesToAdd.map((file) => {
        return {
          type: file.type ?? "",
          name: file.name,
          secureName: file.secureName,
        };
      });

      setAttachments((prev) => [...prev, ...newAttachments]);
    },
    [setAttachments]
  );

  // Remove an attachment by secureName
  const removeAttachment = useCallback(
    (secureName: string) => {
      setAttachments((prev) => prev.filter((attachment) => attachment.secureName !== secureName));
    },
    [setAttachments]
  );

  // Clear all attachments
  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, [setAttachments]);

  return {
    attachments,
    files,
    isLoading,
    isError,
    loadingCount,
    uploadFiles,
    addExistingFiles,
    removeAttachment,
    clearAttachments,
    uploadState: {
      stage: uploadFileMutation.stage,
      progress: uploadFileMutation.progress || uploadProgress,
    },
  };
};
